import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from '@/components/base/Logo';
import { resolveLogoVariant } from '@/assets/brand';

const serviceLinks = [
  { name: 'Web Development', slug: 'web-development', icon: 'ri-global-line' },
  { name: 'Mobile App Development', slug: 'mobile-app-development', icon: 'ri-smartphone-line' },
  { name: 'Digital Marketing', slug: 'digital-marketing', icon: 'ri-megaphone-line' },
  { name: 'UI/UX Design', slug: 'ui-ux-design', icon: 'ri-palette-line' },
  { name: 'Cloud Solutions', slug: 'cloud-solutions', icon: 'ri-cloud-line' },
  { name: 'IT Consulting', slug: 'it-consulting', icon: 'ri-customer-service-line' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const logoVariant = resolveLogoVariant(location.pathname, isScrolled);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path.startsWith('/#')) return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    if (href.startsWith('/#')) {
      navigate('/');
      setTimeout(() => {
        const id = href.replace('/#', '');
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(href);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/85 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <div className="section-padding py-2 md:py-2.5 flex items-center justify-between gap-4">
        <Logo variant={logoVariant} size="md" />

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1 bg-background-50/60 backdrop-blur-sm border border-background-200/70 rounded-full px-2 py-1">
          <button
            onClick={() => handleNavClick('/')}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              isActive('/') ? 'bg-foreground-900 text-background-50' : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            Home
          </button>
          <button
            onClick={() => handleNavClick('/#about')}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              isActive('/#about') ? 'bg-foreground-900 text-background-50' : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            About
          </button>
          <div ref={servicesRef} className="relative">
            <button
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                isServicesOpen || location.pathname.startsWith('/services')
                  ? 'bg-foreground-900 text-background-50'
                  : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              Services
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isServicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 rounded-2xl border border-background-200 overflow-hidden z-50 py-2">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    onClick={() => setIsServicesOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-background-100 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500 transition-colors">
                      <i className={`${s.icon} text-primary-500 group-hover:text-background-50 text-sm`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">{s.name}</span>
                  </Link>
                ))}
                <div className="px-5 py-3 border-t border-background-200">
                  <button
                    onClick={() => { setIsServicesOpen(false); handleNavClick('/#services'); }}
                    className="text-xs text-primary-500 font-medium hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    View All Services <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => handleNavClick('/#portfolio')}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              isActive('/#portfolio') ? 'bg-foreground-900 text-background-50' : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            Portfolio
          </button>
          <button
            onClick={() => handleNavClick('/#blog')}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              isActive('/#blog') ? 'bg-foreground-900 text-background-50' : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            Blog
          </button>
          <button
            onClick={() => handleNavClick('/#faq')}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              isActive('/#faq') ? 'bg-foreground-900 text-background-50' : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            FAQ
          </button>
          <button
            onClick={() => handleNavClick('/#contact')}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
              isActive('/#contact') ? 'bg-foreground-900 text-background-50' : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
            }`}
          >
            Contact
          </button>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => handleNavClick('/#contact')}
            className="group inline-flex items-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            Get Started
            <i className="ri-arrow-right-up-line text-base group-hover:translate-x-0.5 transition-transform"></i>
          </button>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md bg-background-50/70 border border-background-200 cursor-pointer"
          aria-label="Toggle menu"
        >
          <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl text-foreground-900`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-background-50 border-b border-background-200 transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="section-padding py-4 space-y-1">
          <button
            onClick={() => handleNavClick('/')}
            className="block w-full text-left py-3 px-4 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
          >
            Home
          </button>
          <button
            onClick={() => handleNavClick('/#about')}
            className="block w-full text-left py-3 px-4 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
          >
            About
          </button>
          <div>
            <button
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
              className="flex items-center justify-between w-full py-3 px-4 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
            >
              Services
              <i className={`ri-arrow-down-s-line transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {isMobileServicesOpen && (
              <div className="pl-4 ml-4 border-l-2 border-primary-500/20 space-y-1 mt-1">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 py-2 px-3 rounded-lg text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors"
                  >
                    <i className={`${s.icon} text-primary-500`}></i>
                    {s.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => handleNavClick('/#portfolio')}
            className="block w-full text-left py-3 px-4 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
          >
            Portfolio
          </button>
          <button
            onClick={() => handleNavClick('/#blog')}
            className="block w-full text-left py-3 px-4 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
          >
            Blog
          </button>
          <button
            onClick={() => handleNavClick('/#faq')}
            className="block w-full text-left py-3 px-4 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
          >
            FAQ
          </button>
          <button
            onClick={() => handleNavClick('/#contact')}
            className="block w-full text-left py-3 px-4 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors"
          >
            Contact
          </button>
          <button
            onClick={() => handleNavClick('/#contact')}
            className="block w-full text-center bg-foreground-900 text-background-50 py-3 rounded-lg text-sm font-medium mt-2 cursor-pointer"
          >
            Get Started
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;